import { useState, useRef, useEffect, useMemo, useCallback, type UIEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Toaster } from 'sonner'

import { Message, ChatHistory } from '../chat-page/App'
import { parseTimestamp } from '../lib/dateUtils'
import { useTheme } from '../ThemeProvider'
import { useAuth } from '../auth/AuthProvider'
import { ConfirmationDialog } from '../shared/ConfirmationDialog'
import { RenameDialog } from '../chat-page/components/RenameDialog'

import { Sidebar } from './components/Sidebar'
import { Topbar } from './components/Topbar'
import { ChatMessages } from './components/ChatMessages'
import { NudgeBar } from './components/NudgeBar'
import { InputBar } from './components/InputBar'
import { CHAT_MODEL_GPT4O, DEFAULT_CHAT_MODEL_ID, isLlmConfigChatModelId } from '../lib/llmConfigModels'
import { useAvailableChatModels } from '../lib/useAvailableChatModels'
import { fetchChatSessionsPage, mapApiSessionToChatHistory } from '../lib/chatSessionsApi'
import {
  chatErrorToastFromPayload,
  getChatErrorToastMessage,
  parseChatApiError,
  SESSION_MODEL_BLOCKED_BANNER,
  SESSION_MODEL_BLOCKED_TOAST,
  SESSION_MODEL_RELOCK_SUCCESS_TOAST,
  shouldOfferModelRelock,
} from '../lib/chatApiErrors'
import { ChatModelRelockDialog } from '../chat/ChatModelRelockDialog'
import { SessionTimeout } from './components/SessionTimeout'
import { GuidesView } from './components/GuidesView'
import { SkillsView } from './components/SkillsView'
import { Playbooks } from './components/Playbooks'
import { Toast } from './components/Toast'
import './styles/tokens.css'
import './styles/chat-v2-bundle.css'

const CHAT_MODEL_STORAGE_KEY = 'aira-chat-model'

type SidebarView = 'chat' | 'guides' | 'playbooks' | 'skills'

function sanitizeModel(v: string | null): string {
  return isLlmConfigChatModelId(v) ? v : DEFAULT_CHAT_MODEL_ID
}

// Copied from chat-page/App.tsx (not exported there)
function parseChatStreamPiece(raw: string):
  | { kind: 'title_update'; chatId: string; title: string }
  | { kind: 'response_truncated' }
  | { kind: 'text'; text: string } {
  const trimmed = raw.trim()
  if (trimmed) {
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>
      if (parsed.type === 'title_update' && typeof parsed.chat_id === 'string' && typeof parsed.title === 'string') {
        return { kind: 'title_update', chatId: parsed.chat_id, title: parsed.title }
      }
      if (parsed.type === 'response_truncated') {
        return { kind: 'response_truncated' }
      }
    } catch {
      // plain text chunk
    }
  }
  return { kind: 'text', text: raw }
}

export default function ChatAppV2() {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()
  const { user, credentialUser, dbUser, signOut } = useAuth()

  const [currentChatId, setCurrentChatId] = useState<string | null>(null)
  const [chatHistories, setChatHistories] = useState<ChatHistory[]>([])
  const [isTyping, setIsTyping] = useState(false)
  const [chatModel, setChatModel] = useState<string>(() =>
    sanitizeModel(typeof window !== 'undefined' ? localStorage.getItem(CHAT_MODEL_STORAGE_KEY) : null)
  )

  const { models: availableChatModels } = useAvailableChatModels()

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [view, setView] = useState<SidebarView>('chat')
  const [inactivityOpen, setInactivityOpen] = useState(false)
  const [toastMsg, setToastMsg] = useState('')
  const [toastVisible, setToastVisible] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [chatToDelete, setChatToDelete] = useState<string | null>(null)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [chatToRename, setChatToRename] = useState<string | null>(null)
  const [modelRelockOpen, setModelRelockOpen] = useState(false)
  const [modelRelockChatId, setModelRelockChatId] = useState<string | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const sessionsLoadedRef = useRef(false)
  const nextSessionOffsetRef = useRef(0)
  const [sessionsHasMore, setSessionsHasMore] = useState(false)
  const [sessionsLoadingMore, setSessionsLoadingMore] = useState(false)
  const chatCreationInProgressRef = useRef(false)
  const streamAbortRef = useRef<AbortController | null>(null)

  const prevBreakpointRef = useRef<'mobile' | 'tablet' | 'desktop' | null>(null)

  useEffect(() => {
    function handleResize() {
      const w = window.innerWidth
      if (w > 1024) {
        const entering = prevBreakpointRef.current !== 'desktop'
        prevBreakpointRef.current = 'desktop'
        if (entering) setSidebarCollapsed(false)
        setMobileSidebarOpen(false)
        setIsMobile(false)
      } else if (w > 768) {
        prevBreakpointRef.current = 'tablet'
        setSidebarCollapsed(true)
        setMobileSidebarOpen(false)
        setIsMobile(false)
      } else {
        prevBreakpointRef.current = 'mobile'
        setSidebarCollapsed(false)
        setMobileSidebarOpen(false)
        setIsMobile(true)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

  /** After streaming, replace local messages with DB rows (real ids + Claude skill file attachments). */
  const syncChatFromStreamHistory = useCallback(
    async (chatId: string, pendingTrunc: boolean) => {
      try {
        const historyRes = await fetch(`${API_BASE}chat/stream-history/${chatId}`, { credentials: 'include' })
        if (!historyRes.ok) return
        const historyData = (await historyRes.json()) as {
          history?: Record<string, unknown>[]
          model_name?: string | null
        }
        const history = historyData.history || []
        const lockedModel =
          typeof historyData.model_name === 'string' && historyData.model_name.trim()
            ? historyData.model_name.trim()
            : undefined
        setChatHistories(prev =>
          prev.map(chat => {
            if (chat.id !== chatId) return chat
            return {
              ...chat,
              ...(lockedModel ? { sessionModelId: lockedModel } : {}),
              messages: history.map((h, idx) => ({
                id: String(h.id ?? ''),
                content: String(h.content ?? ''),
                sender: h.role === 'user' ? 'user' : 'aira',
                timestamp: h.timestamp ? parseTimestamp(String(h.timestamp)) : new Date(),
                is_edited: h.is_edited as string | undefined,
                original_content: h.original_content as string | undefined,
                edited_at: h.edited_at ? new Date(String(h.edited_at)) : undefined,
                file_path: h.file_path as string | undefined,
                file_name: h.file_name as string | undefined,
                file_size: h.file_size != null ? String(h.file_size) : undefined,
                responseTruncated:
                  pendingTrunc && idx === history.length - 1 && h.role === 'assistant',
              })),
              timestamp: new Date(),
            }
          })
        )
      } catch {
        console.warn('Failed to reload chat history after streaming')
      }
    },
    [API_BASE]
  )

  const currentChat = chatHistories.find(c => c.id === currentChatId)
  const messages = useMemo(() => {
    return [...(currentChat?.messages ?? [])].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }, [currentChat?.messages])

  const msgCount = messages.filter(m => m.sender === 'user').length

  const sessionModelBlocked = useMemo(() => {
    const mid = currentChat?.sessionModelId
    if (!mid || availableChatModels.length === 0) return false
    const ids = new Set(availableChatModels.map(m => m.id))
    return !ids.has(mid)
  }, [currentChat?.sessionModelId, availableChatModels])

  /** Keep chatModel on the session id even when that id is off the allowlist (disabled model). */
  useEffect(() => {
    if (!availableChatModels.length) return
    const ids = new Set(availableChatModels.map(m => m.id))
    if (ids.has(chatModel)) return
    const locked = currentChatId
      ? chatHistories.find(c => c.id === currentChatId)?.sessionModelId
      : undefined
    if (locked && chatModel === locked) return
    setChatModel(availableChatModels[0].id)
  }, [availableChatModels, chatModel, currentChatId, chatHistories])

  const displayName = (dbUser?.display_name || dbUser?.first_name || credentialUser?.username || user?.displayName || 'User').trim()
  const displayFirstName = (displayName.includes('@') ? displayName.split('@')[0] : displayName).split(' ')[0] || 'User'
  const initials = (displayName.includes('@') ? displayName.split('@')[0] : displayName)
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg); setToastVisible(true)
  }, [])

  useEffect(() => { localStorage.setItem(CHAT_MODEL_STORAGE_KEY, chatModel) }, [chatModel])

  /** When opening a chat, show that session's locked model — not the last globally selected model. */
  useEffect(() => {
    if (!currentChatId || !availableChatModels.length) return
    const chat = chatHistories.find(c => c.id === currentChatId)
    const mid = chat?.sessionModelId
    if (!mid) return
    setChatModel(mid)
  }, [currentChatId, chatHistories, availableChatModels])

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => { scrollToBottom() }, [messages, scrollToBottom])

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimeoutRef.current)
    inactivityTimeoutRef.current = setTimeout(() => setInactivityOpen(true), 15 * 60 * 1000)
  }, [])

  useEffect(() => {
    resetInactivityTimer()
    const events = ['mousemove', 'keydown', 'click', 'scroll']
    events.forEach(e => window.addEventListener(e, resetInactivityTimer))
    return () => {
      clearTimeout(inactivityTimeoutRef.current)
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer))
    }
  }, [resetInactivityTimer])

  const persistNewChat = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}create-initial-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `Chat ${new Date().toLocaleString('en-GB', { timeZone: 'Asia/Kolkata' })}`,
          model_name: chatModel,
        }),
        credentials: 'include',
      })
      if (!res.ok) {
        const msg = await getChatErrorToastMessage(res, 'Failed to create chat session')
        throw new Error(msg)
      }
      const data = (await res.json()) as { id: unknown; name?: string; model_name?: string | null }
      const { id, name } = data
      const messagesBeforeUser: Message[] = []
      const sid =
        typeof data.model_name === 'string' && data.model_name.trim()
          ? data.model_name.trim()
          : undefined
      const newChat: ChatHistory = {
        id: String(id),
        title: name || 'New Chat',
        messages: [],
        timestamp: new Date(),
        ...(sid ? { sessionModelId: sid } : {}),
      }
      setChatHistories(prev => [newChat, ...prev])
      setCurrentChatId(String(id))
      setView('chat')
      return { chatId: String(id), messagesBeforeUser }
    } catch {
      throw new Error('Failed to create chat session')
    }
  }, [API_BASE, chatModel])

  const startDraftChat = useCallback(() => {
    streamAbortRef.current?.abort()
    streamAbortRef.current = null
    setIsTyping(false)
    setCurrentChatId(null)
    setView('chat')
    const nextModel =
      availableChatModels.length > 0
        ? (new Set(availableChatModels.map((m) => m.id)).has(CHAT_MODEL_GPT4O)
          ? CHAT_MODEL_GPT4O
          : availableChatModels[0].id)
        : CHAT_MODEL_GPT4O
    setChatModel(nextModel)
  }, [availableChatModels])

  const loadMoreSessions = useCallback(async () => {
    if (!sessionsHasMore || sessionsLoadingMore) return
    setSessionsLoadingMore(true)
    try {
      const offset = nextSessionOffsetRef.current
      const { sessions, has_more } = await fetchChatSessionsPage(API_BASE, offset)
      const mapped = sessions.map(row => mapApiSessionToChatHistory(row)) as ChatHistory[]
      setChatHistories(prev => {
        const seen = new Set(prev.map(c => c.id))
        const merged = [...prev]
        for (const c of mapped) {
          if (!seen.has(c.id)) {
            seen.add(c.id)
            merged.push(c)
          }
        }
        return merged
      })
      nextSessionOffsetRef.current = offset + sessions.length
      setSessionsHasMore(has_more)
    } catch {
      // keep existing list
    } finally {
      setSessionsLoadingMore(false)
    }
  }, [API_BASE, sessionsHasMore, sessionsLoadingMore])

  useEffect(() => {
    if (sessionsLoadedRef.current) return
    sessionsLoadedRef.current = true
    const load = async () => {
      try {
        nextSessionOffsetRef.current = 0
        const { sessions, has_more } = await fetchChatSessionsPage(API_BASE, 0)
        setSessionsHasMore(has_more)
        if (sessions.length > 0) {
          const mapped = sessions.map(row => mapApiSessionToChatHistory(row)) as ChatHistory[]
          setChatHistories(mapped)
          nextSessionOffsetRef.current = sessions.length
          setCurrentChatId(mapped[0].id)
        }
      } catch {
        // If session load fails, keep welcome state without creating a backend chat.
      }
    }
    load()
  }, [API_BASE])

  const handleNavListScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget
      if (!sessionsHasMore || sessionsLoadingMore) return
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
        void loadMoreSessions()
      }
    },
    [sessionsHasMore, sessionsLoadingMore, loadMoreSessions]
  )

  useEffect(() => {
    const state = location.state as { initialPrompt?: string; initialView?: SidebarView } | undefined
    if (state?.initialView) {
      // If another route wants to open a specific sidebar view (e.g. Playbooks), do it once then clear state.
      setView(state.initialView)
      navigate('/chat', { replace: true })
      return
    }
    if (state?.initialPrompt) {
      navigate('/chat', { replace: true })
      setTimeout(() => sendMessage(state.initialPrompt!), 200)
    }
  }, [location.state, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleStop = useCallback(() => {
    streamAbortRef.current?.abort()
    streamAbortRef.current = null
    setIsTyping(false)
  }, [])

  const sendMessage = useCallback(async (content: string, file?: File, opts?: { useAgentSkills?: boolean; selectedSkillIds?: string[] }) => {
    // Abort any in-flight stream
    streamAbortRef.current?.abort()
    streamAbortRef.current = null

    if (sessionModelBlocked && currentChatId) {
      setModelRelockChatId(currentChatId)
      setModelRelockOpen(true)
      showToast(SESSION_MODEL_BLOCKED_TOAST)
      return
    }

    let chatIdToUse = currentChatId
    let messagesBeforeUser: Message[] = []
    if (!chatIdToUse && !chatCreationInProgressRef.current) {
      chatCreationInProgressRef.current = true
      try {
        try {
          const created = await persistNewChat()
          chatIdToUse = created.chatId
          messagesBeforeUser = created.messagesBeforeUser
        } catch (err: unknown) {
          const fallback = 'Failed to start a new chat. Please try again.'
          showToast(err instanceof Error && err.message ? err.message : fallback)
          return
        }
      } finally {
        chatCreationInProgressRef.current = false
      }
    }
    if (!chatIdToUse) return
    const userMessage: Message = { id: Date.now().toString(), content, sender: 'user', timestamp: new Date() }
    setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
      ? { ...chat, messages: [...chat.messages, userMessage], timestamp: new Date() }
      : chat
    ))
    setIsTyping(true)
    setView('chat')
    resetInactivityTimer()

    const airaId = `aira_${Date.now()}`
    let streamingAiraId: string | null = airaId
    // Placeholder assistant before network so UI never shows a detached typing row.
    setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
      ? {
        ...chat,
        messages: [...chat.messages, { id: airaId, content: '', sender: 'aira' as const, timestamp: new Date(), isStreaming: true }],
        timestamp: new Date(),
      }
      : chat
    ))

    const controller = new AbortController()
    streamAbortRef.current = controller

    try {
      const chatToUse = chatHistories.find(c => c.id === chatIdToUse)
      const historyMessages = (chatToUse?.messages ?? messagesBeforeUser)
      const historyForBackend = [...historyMessages, userMessage].map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.content,
      }))
      const formData = new FormData()
      formData.append('message', content)
      formData.append('history', JSON.stringify(historyForBackend))
      formData.append('chat_id', chatIdToUse!)
      formData.append('chat_model', chatModel)
      if (opts?.useAgentSkills) formData.append('use_agent_skills', 'true')
      if (opts?.selectedSkillIds?.length) formData.append('selected_skill_ids', JSON.stringify(opts.selectedSkillIds))
      if (file) formData.append('file', file)

      const res = await fetch(`${API_BASE}chat/stream`, {
        method: 'POST', body: formData, credentials: 'include',
        signal: controller.signal,
      })
      if (!res.ok) {
        const payload = await parseChatApiError(res)
        if (shouldOfferModelRelock(payload) && chatIdToUse) {
          setModelRelockChatId(chatIdToUse)
          setModelRelockOpen(true)
        } else {
          showToast(chatErrorToastFromPayload(payload, 'Failed to get a response. Please try again.'))
        }
        if (streamingAiraId) {
          setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
            ? { ...chat, messages: chat.messages.filter(m => m.id !== streamingAiraId) }
            : chat
          ))
        }
        return
      }
      if (!res.body) {
        showToast('Failed to get a response. Please try again.')
        if (streamingAiraId) {
          setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
            ? { ...chat, messages: chat.messages.filter(m => m.id !== streamingAiraId) }
            : chat
          ))
        }
        return
      }

      let assistantCreated = true
      let hitMaxTokens = false

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        // Backend delimiter: --|\n\n  (same as v1)
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split('--|\n\n')
        buffer = parts.pop() ?? ''
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue
          const piece = parseChatStreamPiece(part.slice(6))
          if (piece.kind === 'text') {
            if (!assistantCreated) {
              assistantCreated = true
              setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
                ? { ...chat, messages: [...chat.messages, { id: airaId, content: '', sender: 'aira' as const, timestamp: new Date() }], timestamp: new Date() }
                : chat
              ))
              if (hitMaxTokens) {
                setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
                  ? { ...chat, messages: chat.messages.map(m => m.id === airaId ? { ...m, responseTruncated: true } : m) }
                  : chat
                ))
              }
            }
            setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
              ? { ...chat, messages: chat.messages.map(m => m.id === airaId ? { ...m, content: m.content + piece.text } : m) }
              : chat
            ))
          } else if (piece.kind === 'title_update') {
            setChatHistories(prev => prev.map(chat => chat.id === piece.chatId ? { ...chat, title: piece.title } : chat))
          } else if (piece.kind === 'response_truncated') {
            hitMaxTokens = true
            if (assistantCreated) {
              setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
                ? { ...chat, messages: chat.messages.map(m => m.id === airaId ? { ...m, responseTruncated: true } : m) }
                : chat
              ))
            }
          }
        }
      }
      await syncChatFromStreamHistory(chatIdToUse, hitMaxTokens)
    } catch (err: unknown) {
      // Ignore abort errors (user clicked stop)
      if (err instanceof Error && err.name === 'AbortError') return
      const fallback = 'Failed to get a response. Please try again.'
      showToast(err instanceof Error && err.message ? err.message : fallback)
      if (streamingAiraId) {
        setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
          ? { ...chat, messages: chat.messages.filter(m => m.id !== streamingAiraId) }
          : chat
        ))
      }
    } finally {
      setIsTyping(false)
      // Ensure streaming flag is cleared even if we early-exit.
      if (streamingAiraId) {
        setChatHistories(prev => prev.map(chat => chat.id === chatIdToUse
          ? { ...chat, messages: chat.messages.map(m => m.id === streamingAiraId ? { ...m, isStreaming: false } : m) }
          : chat
        ))
      }
      if (streamAbortRef.current === controller) streamAbortRef.current = null
    }
  }, [sessionModelBlocked, currentChatId, chatHistories, chatModel, API_BASE, persistNewChat, resetInactivityTimer, showToast, syncChatFromStreamHistory])

  const editMessage = useCallback(async (messageId: string, newContent: string) => {
    if (!currentChatId) return

    if (sessionModelBlocked) {
      setModelRelockChatId(currentChatId)
      setModelRelockOpen(true)
      showToast(SESSION_MODEL_BLOCKED_TOAST)
      return
    }

    // Abort any in-flight stream
    streamAbortRef.current?.abort()
    streamAbortRef.current = null

    const chat = chatHistories.find(c => c.id === currentChatId)
    const originalMessages = chat?.messages ?? []
    const editedIndex = originalMessages.findIndex(m => m.id === messageId)
    if (editedIndex < 0) return

    setIsTyping(true)
    setView('chat')
    resetInactivityTimer()

    // Remove the edited user message and everything after it.
    setChatHistories(prev => prev.map(c => {
      if (c.id !== currentChatId) return c
      return { ...c, messages: c.messages.slice(0, editedIndex) }
    }))

    const newUserMessage: Message = { id: Date.now().toString(), content: newContent, sender: 'user', timestamp: new Date() }
    setChatHistories(prev => prev.map(c => c.id === currentChatId
      ? { ...c, messages: [...c.messages, newUserMessage], timestamp: new Date() }
      : c
    ))

    const airaId = `edit_aira_${Date.now()}`
    const streamingAiraId: string | null = airaId
    setChatHistories(prev => prev.map(c => c.id === currentChatId
      ? { ...c, messages: [...c.messages, { id: airaId, content: '', sender: 'aira' as const, timestamp: new Date(), isStreaming: true, isRegenerating: true }] }
      : c
    ))

    const controller = new AbortController()
    streamAbortRef.current = controller

    try {
      const formData = new FormData()
      formData.append('message_id', messageId)
      formData.append('new_content', newContent)
      formData.append('chat_id', currentChatId)
      formData.append('chat_model', chatModel)

      const res = await fetch(`${API_BASE}chat/edit-message`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        signal: controller.signal,
      })
      if (!res.ok) {
        const payload = await parseChatApiError(res)
        if (shouldOfferModelRelock(payload) && currentChatId) {
          setModelRelockChatId(currentChatId)
          setModelRelockOpen(true)
        } else {
          showToast(chatErrorToastFromPayload(payload, 'Failed to edit message. Please try again.'))
        }
        setChatHistories(prev => prev.map(c => c.id === currentChatId ? { ...c, messages: originalMessages } : c))
        return
      }
      if (!res.body) {
        showToast('Failed to edit message. Please try again.')
        setChatHistories(prev => prev.map(c => c.id === currentChatId ? { ...c, messages: originalMessages } : c))
        return
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let hitMaxTokens = false
      let assistantCreated = true

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split('--|\n\n')
        buffer = parts.pop() ?? ''
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue
          const piece = parseChatStreamPiece(part.slice(6))
          if (piece.kind === 'title_update') {
            setChatHistories(prev => prev.map(c => c.id === piece.chatId ? { ...c, title: piece.title } : c))
            continue
          }
          if (piece.kind === 'response_truncated') {
            hitMaxTokens = true
            continue
          }
          if (!assistantCreated) {
            assistantCreated = true
            setChatHistories(prev => prev.map(c => c.id === currentChatId
              ? { ...c, messages: [...c.messages, { id: airaId, content: '', sender: 'aira' as const, timestamp: new Date(), isRegenerating: true }] }
              : c
            ))
          }
          setChatHistories(prev => prev.map(c => c.id === currentChatId
            ? {
              ...c,
              messages: c.messages.map(m => m.id === airaId ? { ...m, content: m.content + piece.text, isRegenerating: false } : m),
            }
            : c
          ))
        }
      }

      if (assistantCreated) {
        setChatHistories(prev => prev.map(c => c.id === currentChatId
          ? { ...c, messages: c.messages.map(m => m.id === airaId ? { ...m, responseTruncated: hitMaxTokens, isRegenerating: false } : m) }
          : c
        ))
      }
      await syncChatFromStreamHistory(currentChatId, hitMaxTokens)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return
      setChatHistories(prev => prev.map(c => c.id === currentChatId ? { ...c, messages: originalMessages } : c))
      const fallback = 'Failed to edit message. Please try again.'
      showToast(err instanceof Error && err.message ? err.message : fallback)
    } finally {
      setIsTyping(false)
      if (streamingAiraId) {
        setChatHistories(prev => prev.map(c => c.id === currentChatId
          ? { ...c, messages: c.messages.map(m => m.id === streamingAiraId ? { ...m, isStreaming: false } : m) }
          : c
        ))
      }
      if (streamAbortRef.current === controller) streamAbortRef.current = null
    }
  }, [
    API_BASE,
    chatHistories,
    chatModel,
    currentChatId,
    resetInactivityTimer,
    sessionModelBlocked,
    showToast,
    syncChatFromStreamHistory,
  ])

  const regenerate = useCallback((messageId: string) => {
    if (!currentChatId) return
    if (sessionModelBlocked) {
      setModelRelockChatId(currentChatId)
      setModelRelockOpen(true)
      showToast(SESSION_MODEL_BLOCKED_TOAST)
      return
    }
    const chat = chatHistories.find(c => c.id === currentChatId)
    if (!chat) return
    const idx = chat.messages.findIndex(m => m.id === messageId)
    if (idx < 1) return
    const prevUser = [...chat.messages].slice(0, idx).reverse().find(m => m.sender === 'user')
    if (prevUser) sendMessage(prevUser.content)
  }, [currentChatId, chatHistories, sendMessage, sessionModelBlocked, showToast])

  const handleLogOut = useCallback(async () => {
    try {
      await fetch(`${API_BASE}logout`, { method: 'POST', credentials: 'include' })
    } catch {
      void 0
    }
    await signOut()
    navigate('/')
  }, [API_BASE, navigate, signOut])

  const handleChipClick = useCallback((text: string) => {
    sendMessage(text)
  }, [sendMessage])

  const handleTryPrompt = useCallback((text: string) => {
    setView('chat')
    sendMessage(text)
  }, [sendMessage])

  const modelLocked = messages.some(m => m.sender === 'user')

  const handleDeleteChat = useCallback((chatId: string) => {
    setChatToDelete(chatId)
    setDeleteDialogOpen(true)
  }, [])

  const confirmDeleteChat = useCallback(async () => {
    if (!chatToDelete) return
    try {
      await fetch(`${API_BASE}chat/sessions/${chatToDelete}`, { method: 'DELETE', credentials: 'include' })
    } catch {
      // ignore delete error
    }

    setChatHistories(prev => {
      const filtered = prev.filter(chat => chat.id !== chatToDelete)
      if (currentChatId === chatToDelete) {
        if (filtered.length > 0) {
          setCurrentChatId(filtered[0].id)
        } else {
          setCurrentChatId(null)
        }
      }
      return filtered
    })

    setDeleteDialogOpen(false)
    setChatToDelete(null)
  }, [API_BASE, chatToDelete, currentChatId])

  const handleRenameChat = useCallback((chatId: string) => {
    setChatToRename(chatId)
    setRenameDialogOpen(true)
  }, [])

  const confirmRenameChat = useCallback(async (newTitle: string) => {
    if (!chatToRename) return
    try {
      const res = await fetch(`${API_BASE}chat/sessions/${chatToRename}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTitle }),
        credentials: 'include',
      })
      if (!res.ok) throw new Error('rename failed')
      setChatHistories(prev => prev.map(chat => chat.id === chatToRename ? { ...chat, title: newTitle } : chat))
    } catch {
      showToast('Failed to rename chat.')
    }
    setRenameDialogOpen(false)
    setChatToRename(null)
  }, [API_BASE, chatToRename, showToast])

  return (
    <div className="chat-app-root" style={{ display: 'flex', flexDirection: 'column', background: 'var(--bg-canvas)', color: 'var(--text-primary)', overflow: 'hidden', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Toaster position="top-center" />
      <Toast message={toastMsg} visible={toastVisible} onHide={() => setToastVisible(false)} />
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete chat?"
        description="This will remove the chat from your list. You can’t undo this action."
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDeleteChat}
      />
      <RenameDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        currentTitle={chatHistories.find(c => c.id === chatToRename)?.title ?? ''}
        onConfirm={confirmRenameChat}
      />
      <ChatModelRelockDialog
        key={modelRelockChatId ?? 'relock'}
        open={modelRelockOpen}
        onOpenChange={o => {
          setModelRelockOpen(o)
          if (!o) setModelRelockChatId(null)
        }}
        apiBase={API_BASE}
        chatId={modelRelockChatId}
        modelOptions={availableChatModels}
        onRequestError={showToast}
        onRelocked={(cid, modelId) => {
          setChatHistories(prev => prev.map(c => (c.id === cid ? { ...c, sessionModelId: modelId } : c)))
          setChatModel(modelId)
          showToast(SESSION_MODEL_RELOCK_SUCCESS_TOAST)
        }}
      />

      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(c => !c)}
          mobileOpen={mobileSidebarOpen}
          chatHistories={chatHistories}
          currentChatId={currentChatId}
          onNewChat={() => { startDraftChat(); setMobileSidebarOpen(false) }}
          onSelectChat={id => { setCurrentChatId(id); setView('chat'); setMobileSidebarOpen(false) }}
          onDeleteChat={handleDeleteChat}
          onRenameChat={handleRenameChat}
          view={view}
          onSetView={v => { setView(v); setMobileSidebarOpen(false) }}
          userName={displayName}
          userInitials={initials}
          theme={theme as 'dark' | 'light'}
          onToggleTheme={toggleTheme}
          onLogOut={handleLogOut}
          sessionsLoadingMore={sessionsLoadingMore}
          onNavListScroll={handleNavListScroll}
        />

        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Topbar
            chatTitle={
              view === 'playbooks' ? 'Playbooks'
              : view === 'skills' ? 'Skills'
              : view === 'guides' ? 'Guides'
              : currentChat?.title?.slice(0, 40) || 'New conversation'
            }
            msgCount={msgCount}
            onMobileMenuClick={isMobile ? () => setMobileSidebarOpen(o => !o) : undefined}
          />
          {view === 'guides' ? (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <GuidesView onTryPrompt={handleTryPrompt} />
            </div>
          ) : view === 'skills' ? (
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <SkillsView />
            </div>
          ) : view === 'playbooks' ? (
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Playbooks />
            </div>
          ) : (
            <>
              <ChatMessages
                messages={messages}
                isTyping={isTyping}
                messagesEndRef={messagesEndRef}
                onRegenerate={regenerate}
                onContinueResponse={() => {
                  void sendMessage('continue')
                }}
                onEditMessage={editMessage}
                userName={displayFirstName}
                userInitials={initials}
                onChipClick={handleChipClick}
              />
              {sessionModelBlocked && currentChatId ? (
                <div className="hard-stop" role="alert">
                  <span>{SESSION_MODEL_BLOCKED_BANNER}</span>
                  <button
                    type="button"
                    className="underline underline-offset-2 ml-2 font-medium"
                    style={{ color: 'var(--accent)' }}
                    onClick={() => {
                      setModelRelockChatId(currentChatId)
                      setModelRelockOpen(true)
                    }}
                  >
                    Choose model
                  </button>
                </div>
              ) : null}
              <NudgeBar msgCount={msgCount} />
              <InputBar
                onSendMessage={sendMessage}
                disabled={msgCount >= 80 || sessionModelBlocked}
                isStreaming={isTyping}
                onStop={handleStop}
                chatModel={chatModel}
                onChatModelChange={setChatModel}
                modelLocked={modelLocked || sessionModelBlocked}
                onToast={showToast}
                chatModelOptions={availableChatModels}
              />
            </>
          )}
        </div>
      </div>
      {mobileSidebarOpen && (
        <button
          type="button"
          className="sb-backdrop"
          aria-label="Close navigation"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      <SessionTimeout
        open={inactivityOpen}
        onStayLoggedIn={() => { setInactivityOpen(false); resetInactivityTimer() }}
        onLogOut={handleLogOut}
      />
    </div>
  )
}
